import React from 'react';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import { Edit, Delete, Description, InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useFavoriteProposalsPageStore } from 'store/pages/tools/favorite-proposals/pageStore';
import { Cart } from 'types/cartServiceV2';
import { TableActionsDropdown } from 'components/common/general/TableActionsDropdown';
import { StapleTableRow, StapleTableCell, Tooltip, colors } from '@insurancemenu/imenu-components-lib';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import {
  sortByProductLineId,
  getProductLinesFromArray,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { observer } from 'mobx-react-lite';
import { ActionCellStyled, ProposalNameWrapperStyled } from './styles';

interface MoreDropdownProps {
  cart: Cart;
}

interface FavoriteProposalsRowProps {
  cart: Cart;
}

const MoreDropdown = ({ cart }: MoreDropdownProps): JSX.Element => {
  const { openDeleteModal, openRenameModal, redirectToBrowsing } = useFavoriteProposalsPageStore();
  const { id: cartId, cartName, groupId } = cart;

  const dropdownOptions: DropdownOptionType[] = [
    {
      value: 'editProposalTemplate',
      onOptionClick: (): void => redirectToBrowsing(cartId, groupId),
      label: 'Edit Proposal',
      icon: Edit,
    },
    {
      value: 'renameProposalTemplate',
      label: 'Rename Proposal',
      icon: Description,
      onOptionClick: (): void => {
        openRenameModal(cartName, cartId);
      },
    },
    {
      value: 'removeProposalTemplate',
      label: 'Delete Proposal',
      customColor: 'red',
      icon: Delete,
      onOptionClick: (): void => {
        openDeleteModal(cartName, cartId);
      },
    },
  ];

  return (
    <TableActionsDropdown
      name="More Options"
      options={dropdownOptions}
      optionsWidth="20rem"
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
    />
  );
};

const FavoriteProposalsRow = observer(({ cart }: FavoriteProposalsRowProps): JSX.Element => {
  const { redirectToBrowsing } = useFavoriteProposalsPageStore();

  const { id, cartName, lastUpdateTime, cartItems, groupId } = cart;
  const productLineIds = [...new Set(cartItems.map(({ productLineId }) => productLineId).sort(sortByProductLineId))];

  return (
    <StapleTableRow key={id}>
      <StapleTableCell width="12rem" onClick={(): void => redirectToBrowsing(id, groupId)}>
        <ProposalNameWrapperStyled>
          {cartName}
          {productLineIds.length === 0 && (
            <Tooltip
              label={
                <span>
                  This proposal cannot be used as the basis for a new proposal
                  <br /> until it includes at least one plan
                </span>
              }
            >
              <InfoButton width="2rem" height="2rem" color={colors.red[600]} />
            </Tooltip>
          )}
        </ProposalNameWrapperStyled>
      </StapleTableCell>
      <StapleTableCell width="8rem">{getProductLinesFromArray(productLineIds)}</StapleTableCell>
      <StapleTableCell width="6rem">{dateFormatters.normDateToAmericanDateTime(lastUpdateTime)}</StapleTableCell>
      <ActionCellStyled width="5rem">
        <MoreDropdown cart={cart} />
      </ActionCellStyled>
    </StapleTableRow>
  );
});

export default FavoriteProposalsRow;
